import * as React from 'react';
import { connect } from 'react-redux';
import { get, isEmpty, isNil } from 'lodash';

import Spinner from 'components/spinner/Spinner';
import Page from 'components/page/Page';
import SettingsMessage from 'components/settings-message/SettingsMessage';
import InfluencerComposedMessage from 'components/influencer-composed-message/InfluencerComposedMessage';

import { fetchSubcampaign, resetSubcampaign } from '../reducers/subcampaign';
import { fetchOrganizationSettings, resetOrganizationSettings } from '../../organizations/reducers/organizationSettings';
import { fetchBrandProfile, resetBrandProfile } from 'modules/brands/reducers/brandProfile';
import { fetchInfluencerProfile, resetInfluencerProfile } from 'modules/influencers/reducers/influencerProfile';
import { fetchVariablePayments, resetVariablePayments } from 'modules/subcampaigns/reducers/variable-payments/variable-payments';
import {
  fetchCampaignOverview,
  resetCampaignOverview
} from 'modules/campaigns/reducers/campaignOverview';

import {
  fetchCampaignBrief,
  resetCampaignBrief
} from 'modules/campaigns/reducers/campaignBrief';

import {
  fetchOfferDeliverables,
  resetOfferDeliverables
} from 'modules/subcampaigns/reducers/offerDeliverables';

import {
  searchOrganization,
  selectOrganizationById
} from 'modules/organizations/reducers/organizations';

import { trackCampaignSwitch } from 'modules/tracking/utils/tracking';

import { getCampaignSubnavigation, getSubcampaignSubnavigation } from 'utils/navigation';
import {
  isBrandsFocusedOrganization,
  isInfluencerSettingsPageIncomplete,
  isInfluencersFocusedOrganizationRejected
} from 'utils/organization';
import { logError } from 'utils/log';


import type {
  Account,
  CampaignOverview,
  InfluencerProfile,
  Organization,
  OrganizationSettings,
  Subcampaign
} from 'types';

type OwnProps = {
  params: {
    id: string,
    subcampaignId: string
  },
  documentTitle: string,
  subPage?: 'offer-view' | 'default',
  children: React.ReactNode | undefined | null,
  renderHeader?: (a: boolean) => React.ReactNode,
  renderFooter?: (a: boolean) => React.ReactNode
};

type StateProps = {
  subcampaign: Subcampaign,
  subcampaignIsLoading: boolean,
  brandProfileNotLoaded: boolean,
  campaignIsLoading: boolean,
  offerDeliverablesIsLoading: boolean,
  isForBrand: boolean,
  currentAccount: Account,
  organization: Organization, // eslint-disable-line react/no-unused-prop-types,
  currentOrganizationId: number,
  overview: CampaignOverview | undefined | null,
  organizationSettings: OrganizationSettings,
  campaignProductListAccessIds: string
};

type DispatchProps = {
  dispatchFetchInfluencerProfile: (a: number) => Promise<InfluencerProfile>,
  dispatchResetInfluencerProfile: () => void,
  dispatchFetchSubcampaign: (a: number, params: { [x:string]: boolean }) => Promise<void>,
  dispatchResetSubcampaign: () => Promise<void>,
  dispatchFetchVariablePayments: (b: number, a: number) => Promise<void>,
  dispatchResetVariablePayments: () => Promise<void>,
  dispatchFetchBrandProfile: (a: string) => Promise<void>,
  dispatchResetBrandProfile: () => Promise<void>,
  dispatchFetchCampaignOverview: (a: number) => Promise<void>,
  dispatchResetCampaignOverview: () => Promise<void>,
  dispatchFetchCampaignBrief: (a: number) => Promise<void>,
  dispatchResetCampaignBrief: () => Promise<void>,
  dispatchFetchOfferDeliverables: (a: number) => Promise<void>,
  dispatchResetOfferDeliverables: () => Promise<void>,
  // eslint-disable-next-line react/no-unused-prop-types
  dispatchFetchOrganizationSettings: (a: number) => Promise<void>, // false positive,
  dispatchResetOrganizationSettings: () => Promise<void>,
  dispatchSearchOrganization: (b: string, a: number) => Promise<Organization | null>,
  dispatchSelectOrganizationById: (a: number) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

class BaseSubcampaignPage extends React.PureComponent<Props> {
  componentDidMount() {
    const { params, organization, currentAccount } = this.props;
    const campaignId = Number(params.id);

    if (campaignId) {
      trackCampaignSwitch(currentAccount, organization, campaignId);
    }

    this.fetchCampaignData();
    this.fetchSettings(this.props);
  }

  componentDidUpdate(prevProps: Props) {
    const {
      subcampaignIsLoading,
      isForBrand,
      params,
      currentOrganizationId,
      subPage,
      dispatchFetchInfluencerProfile,
      dispatchFetchSubcampaign,
      dispatchFetchCampaignOverview,
      dispatchFetchVariablePayments,
      dispatchFetchOfferDeliverables,
      dispatchSearchOrganization,
      dispatchSelectOrganizationById
    } = this.props;

    if (prevProps.params.subcampaignId !== params.subcampaignId || prevProps.subPage !== subPage) {
      const subcampaignParams = {
        showLoading: true,
        reset: true,
        includeDetails: subPage === 'offer-view'
      };
      dispatchFetchSubcampaign(Number(params.subcampaignId), subcampaignParams);
      dispatchFetchVariablePayments(Number(params.id), Number(params.subcampaignId));
    }

    const campaignId = Number(params.id);
    const subcampaignId = get(this.props, 'subcampaign.id', null);
    const isSubcampaignLoaded = prevProps.subcampaignIsLoading && !subcampaignIsLoading && subcampaignId;
    const influencerProfileId: any = get(this.props, 'subcampaign.creatorProfile.id', null);
    const offerId: any = get(this.props, 'subcampaign.offer.id', null);
    const updatedOfferId = get(this.props, 'subcampaign.updatedOffer.id', null);

    if (isSubcampaignLoaded && campaignId) {
      dispatchFetchOfferDeliverables(offerId);

      if (updatedOfferId) {
        dispatchFetchOfferDeliverables(updatedOfferId);
      }

      if (isForBrand) {
        dispatchFetchCampaignOverview(campaignId);
        dispatchSearchOrganization('campaign', campaignId)
          .then(organization => {
            if (organization && organization.id) {
              dispatchSelectOrganizationById(organization.id);
            }
          }
          );
      } else {
        dispatchFetchInfluencerProfile(influencerProfileId);
        dispatchSearchOrganization('creator', influencerProfileId)
          .then(organization => {
            if (organization && organization.id) {
              dispatchSelectOrganizationById(organization.id);
            }
          });
      }
    }

    if (currentOrganizationId !== prevProps.currentOrganizationId) {
      this.fetchSettings(this.props);
    }
  }

  componentWillUnmount() {
    const {
      dispatchResetInfluencerProfile,
      dispatchResetSubcampaign,
      dispatchResetVariablePayments,
      dispatchResetBrandProfile,
      dispatchResetCampaignOverview,
      dispatchResetCampaignBrief,
      dispatchResetOfferDeliverables,
      dispatchResetOrganizationSettings
    } = this.props;

    dispatchResetInfluencerProfile();
    dispatchResetSubcampaign();
    dispatchResetVariablePayments();
    dispatchResetBrandProfile();
    dispatchResetCampaignOverview();
    dispatchResetCampaignBrief();
    dispatchResetOfferDeliverables();
    dispatchResetOrganizationSettings();
  }

  async fetchCampaignData() {
    const {
      params,
      subPage,
      dispatchFetchSubcampaign,
      dispatchFetchVariablePayments,
      dispatchFetchCampaignBrief,
      dispatchFetchBrandProfile
    } = this.props;

    const campaignId = Number(params.id);
    if (isNil(campaignId)) {
      logError('Can\'t render SubcampaignPage, id params is required');
      return;
    }

    if (params.subcampaignId) {
      const subcampaignParams = {
        showLoading: true,
        reset: true,
        includeDetails: subPage === 'offer-view'
      };

      dispatchFetchSubcampaign(Number(params.subcampaignId), subcampaignParams);
      dispatchFetchVariablePayments(campaignId, Number(params.subcampaignId));
    }

    try {
      const campaignData = await dispatchFetchCampaignBrief(campaignId);
      const brandProfileId = get(campaignData, 'brandProfileId');

      if (brandProfileId) {
        dispatchFetchBrandProfile(brandProfileId);
      }
    } catch (e) {
      logError(`Failed to fetch required campaign data for ${campaignId}`, e);
    }
  }

  fetchSettings(nextProps: Props) {
    const { currentOrganizationId, dispatchFetchOrganizationSettings } = nextProps;

    dispatchFetchOrganizationSettings(currentOrganizationId);
  }

  isLoading() {
    const {
      subcampaignIsLoading,
      brandProfileNotLoaded,
      campaignIsLoading,
      offerDeliverablesIsLoading
    } = this.props;

    return subcampaignIsLoading || brandProfileNotLoaded || campaignIsLoading || offerDeliverablesIsLoading;
  }

  renderContent() {
    const { children } = this.props;

    return this.isLoading() ?
      <Spinner
        position="inline"
        color="light"
        size="small"
      /> :
      children;
  }

  render(): React.ReactNode {
    const {
      params,
      overview,
      organization,
      subcampaign,
      isForBrand,
      documentTitle,
      renderHeader,
      renderFooter,
      brandProfileNotLoaded,
      campaignIsLoading,
      organizationSettings,
      campaignProductListAccessIds
    } = this.props;

    const { id: campaignId, subcampaignId } = params;
    const isInfluencerOrganizationRejected = isInfluencersFocusedOrganizationRejected(organization);

    return (
      <Page
        documentTitle={documentTitle}
        subnavigation={
          isForBrand
            ? getCampaignSubnavigation(
              campaignId,
              overview,
              organization,
              campaignProductListAccessIds
            )
            : getSubcampaignSubnavigation(campaignId, subcampaignId, subcampaign, organization)}
      >
        {isInfluencerOrganizationRejected && (
          <InfluencerComposedMessage />
        )}
        {
          !isForBrand && isInfluencerSettingsPageIncomplete(organizationSettings) && (
            <SettingsMessage
              organizationId={organizationSettings.id}
              organizationSettings={organizationSettings}
              isSticky
            />
          )
        }
        {renderHeader && renderHeader(this.isLoading())}

        {subcampaignId && this.renderContent() /* do not render anything for the default `campaigns/id/offers` page */}

        {renderFooter && renderFooter(brandProfileNotLoaded || campaignIsLoading)}
      </Page>
    );
  }
}

const mapStateToProps = (
  {
    account,
    subcampaign,
    brandProfile,
    organizations,
    organizationSettings,
    campaignBrief,
    offerDeliverables,
    campaignOverview,
    runtimeConfig
  }
): StateProps => {
  const isForBrand = organizations && isBrandsFocusedOrganization(organizations.currentOrganization);

  return {
    subcampaign: subcampaign.data,
    subcampaignIsLoading: isEmpty(subcampaign.data) || subcampaign.isLoading,
    brandProfileNotLoaded: isNil(brandProfile.profile.id),
    campaignIsLoading: isNil(campaignBrief.brief),
    offerDeliverablesIsLoading: !(subcampaign.data && subcampaign.data.offer && offerDeliverables.deliverablesByOfferId[subcampaign.data.offer.id]),
    isForBrand,
    organization: organizations.currentOrganization,
    currentOrganizationId: organizations.currentOrganizationId,
    currentAccount: account.currentAccount,
    organizationSettings: organizationSettings.organization,
    overview: isForBrand
      ? campaignOverview.overview
      : null,
    campaignProductListAccessIds: runtimeConfig?.config?.campaignProductListAccessIds ?? ''
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchInfluencerProfile: (profileId: number) => dispatch(fetchInfluencerProfile(profileId)),
  dispatchResetInfluencerProfile: () => dispatch(resetInfluencerProfile()),
  dispatchFetchSubcampaign: (subcampaignId: number, params: { [x:string]: boolean }) => dispatch(fetchSubcampaign(subcampaignId, params)),
  dispatchResetSubcampaign: () => dispatch(resetSubcampaign()),
  dispatchFetchVariablePayments: (campaignId: number, subcampaignId: number) => dispatch(fetchVariablePayments(campaignId, subcampaignId)),
  dispatchResetVariablePayments: () => dispatch(resetVariablePayments()),
  dispatchFetchBrandProfile: (brandProfileId: string) => dispatch(fetchBrandProfile(brandProfileId, false)),
  dispatchResetBrandProfile: () => dispatch(resetBrandProfile()),
  dispatchFetchCampaignOverview: (campaignId: number) => dispatch(fetchCampaignOverview(campaignId)),
  dispatchResetCampaignOverview: () => dispatch(resetCampaignOverview()),
  dispatchFetchCampaignBrief: (campaignId: number) => dispatch(fetchCampaignBrief(campaignId, false)),
  dispatchResetCampaignBrief: () => dispatch(resetCampaignBrief()),
  dispatchFetchOfferDeliverables: (offerId: number) => dispatch(fetchOfferDeliverables(offerId, true)),
  dispatchResetOfferDeliverables: () => dispatch(resetOfferDeliverables()),
  dispatchFetchOrganizationSettings: (orgId: number) => dispatch(fetchOrganizationSettings(orgId)),
  dispatchResetOrganizationSettings: () => dispatch(resetOrganizationSettings()),
  dispatchSearchOrganization: (resource: string, resourceId: number) => dispatch(searchOrganization(resource, resourceId)),
  dispatchSelectOrganizationById: (organizationId: number) => dispatch(selectOrganizationById(organizationId))
});

export default connect(mapStateToProps, mapDispatchToProps)(BaseSubcampaignPage);
